import React, {
  useEffect,
  useState,
  createContext,
  useContext,
  ReactElement,
  Children,
  cloneElement,
  useMemo,
} from "react";
import type { SetStateAction } from "react";
import classNames from "classnames";
import styles from "./Hero.module.css";
import _ from "lodash";
import { 
  animationConfigCarouselX1,
   animationConfigCarouselX2, 
   animationConfigCarouselX3,
    animationConfigSlide
   } from "./utils/animationConfigs";
import { X2slide1, X3Slide1, X3Slide2 } from "./utils/hoverAnimationConfigs";

const HeroContext = createContext<SetStateAction<any>>({});

export type HeroProviderProps = {
  type?: string,
  children: React.ReactNode;
  className?: string;
  customPanels?: any;
  width: number;
  isDesktop: boolean;
};

type AnimationState = {
  width: string;
  x?: string;
};

const _Hero = ({
  width,
  isDesktop,
  type = "slide",
  className = "",
  children,
  customPanels,
  ...props
}: HeroProviderProps) => {

  const childrenLen = Children.count(children);
  const calcIndexState = childrenLen > 6 ? [0, 1, 2, 3, 4, 5, 6, 7, 8] : ( childrenLen > 1 ? [0, 1, 2, 3, 4, 5] : [0])

  const animationConfigs = (width, childrenCount, isDesktop) => {
    if (type === "slide") {
      return animationConfigSlide(width, childrenCount)
    }
    if(childrenCount === 1){
      return animationConfigCarouselX1()
    }
    if (childrenCount > 6) {
      return animationConfigCarouselX3(width, childrenCount, isDesktop)
    }
    if (childrenCount === 6) {
      return animationConfigCarouselX2(width, childrenCount, isDesktop)
    }
  }

  const [activeIndex, setActiveIndex] = useState<number>(type === "slide" || childrenLen === 1 ? 0 : childrenLen > 6 ? 3 : 2);
  const [indexState, setIndexState] = useState<number[]>(calcIndexState)

  // Store animation config object in state, so it can be passed into context
  const hoverConfigs = childrenLen === 1 ? null : childrenLen > 6 ? [X3Slide1(width), X3Slide2(width)] : X2slide1(width);
  const [animationConfig, setAnimationConfig] = useState<
    AnimationState[] | null
  >(animationConfigs(width, Children.count(children), isDesktop));
  const [ogConfig] = useState<
  AnimationState[] | null
>(animationConfigs(width, Children.count(children), isDesktop));

  /** this is in an effect, so that it retriggers when `width` changes */
  useEffect(
    () => {
      setAnimationConfig(animationConfigs(width, Children.count(children), isDesktop));
    },
    [width, children]
  );

  /**
   * Pass an index prop onto each child. This is used to store a state
   * object for each panel and track its open and hovered status
   */
  let kids = Children.map(children, (child, index) => {
    return cloneElement(child as ReactElement, { index });
  });

  /**
   * If this is a 1024px < res screen, only return 1 panel
   */
  if (!isDesktop) kids = kids.slice(0, 1);

  const value = useMemo(() => ({
    type,
    activeIndex,
    ogConfig,
    hoverConfigs,
    handleSetActiveIndex: (idx: number) => setActiveIndex(idx),
    handleSetAnimationConfig: (data: any) => setAnimationConfig(data),
    animationConfig,
    indexState,
    childrenLen,
    handleSetIndexState: (data) => setIndexState(data),
    width
  }), [type, activeIndex, indexState, width, animationConfig]);


  return (
    <HeroContext.Provider
      value={value}
      {...props}
    >

      <div id="hero" className={classNames(styles.hero, className)}>
        {kids}
      </div>
    </HeroContext.Provider>
  );
};

export const Hero = React.memo(_Hero);

export const useHeroContext = () => {
  const context = useContext(HeroContext);

  if (!context) throw new Error("Item must be used in HeroContext");

  return context;
};
